const GMT = "GMT";
const UTC = "UTC";
const UTC_ZERO_OFFSET = "UTC+00:00";

export interface TimezoneOption {
    // IANA timezone identifier, e.g. "America/New_York". Used as the form value.
    value: string;
    // Human-readable label shown in the dropdown, e.g. "America/New_York UTC-5".
    label: string;
}

// Returns the UTC offset string for a timezone label, e.g. "UTC+5:30" or "UTC-7".
// Zero-offset timezones (plain "GMT") are normalised to "UTC+00:00".
export const formatOffset = (timezone: string): string => {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en", {
        timeZone: timezone,
        timeZoneName: "shortOffset",
    }).formatToParts(now);
    // The "timeZoneName" part contains the short offset string, e.g. "GMT+5:30" or "GMT-7".
    const offsetString = parts.find((p) => p.type === "timeZoneName")?.value ?? GMT;
    if (offsetString === GMT) {
        return UTC_ZERO_OFFSET;
    }
    return offsetString.replace(GMT, UTC);
};

// Cast to include supportedValuesOf, which is not in the ES2019 dom lib types.
type IntlWithSupportedValues = typeof Intl & { supportedValuesOf(key: "timeZone"): string[] };

const buildTimezoneOptions = (): TimezoneOption[] => {
    const all = (Intl as IntlWithSupportedValues)
        .supportedValuesOf("timeZone")
        .map((tz) => ({ value: tz, label: `${tz} ${formatOffset(tz)}` }));

    // Move "UTC" to the end so it is always the last entry in the dropdown.
    const utcOption = all.find((o) => o.value === UTC);
    const rest = all.filter((o) => o.value !== UTC).sort((a, b) => a.value.localeCompare(b.value));
    return utcOption !== undefined ? [...rest, utcOption] : rest;
};

// Lazily built on first access so the ~600 Intl.DateTimeFormat calls do not
// execute at module import time when the dialog may never be opened.
let cachedTimezoneOptions: TimezoneOption[] | undefined;

export const getTimezoneOptions = (): TimezoneOption[] => {
    if (cachedTimezoneOptions === undefined) {
        cachedTimezoneOptions = buildTimezoneOptions();
    }
    return cachedTimezoneOptions;
};

// Returns the IANA identifier of the browser's local timezone, e.g. "Europe/Helsinki".
// Falls back to "UTC" if the browser throws when resolving timezone information.
export const getLocalTimezone = (): string => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return UTC;
    }
};
